import React from 'react';
import { CheckCircle } from 'lucide-react';

const SAMEER_HIGHLIGHTS = [
  'Carried quota, built pipelines, closed deals, and managed teams.',
  'Hired across the funnel — SDRs, AEs, and revenue leaders.',
  'Pattern recognition based on real performers, not polished resumes.',
  'Focus on "can this person actually sell?" — not "does the resume look good?"',
];

const SELLING_TARGETS = ['Global SMB', 'Mid-market', 'Enterprise clients'];
const TEAM_AREAS = ['New Sales', 'Account Management', 'Customer Success & Renewals', 'Product Management', 'Cross-Functional Leadership'];
const BUILDER_ACHIEVEMENTS = [
  'Successfully set up 2 business units from scratch',
  'Built the first-ever Inside Sales team for Oracle Consulting Services',
];
const SCALING_ENVS = ['On-premise models', 'SaaS environments', 'Cross-Functional Leadership'];
const CROSS_FUNCTIONAL = ['Product Marketing', 'Product Development', 'Implementation & Support', 'Finance & Contracts'];
const GROWTH_IMPACT = [
  'Designed and implemented sales strategies that increased pipeline and revenue streams',
  'Introduced internal processes that improved cross-team efficiency and collaboration',
  'Worked closely with CROs and CEOs for over 13 years in scaling SaaS companies',
];
const ADVISOR_POINTS = [
  'Founded and led his own startup as Founder & CEO',
  'Advisor for 6+ years at Antwalk, mentoring and training various startups on Sales strategy & Product positioning',
  'Also a guest speaker and advisor to early-stage tech founders',
];
const INDUSTRIES = [
  { name: 'Velocity (leading finance platform)', tag: 'B2B' },
  { name: 'Happay Expense (CSM team)', tag: 'B2B' },
  { name: 'OTO Two Wheeler finance', tag: 'B2C' },
  { name: 'Breath Well-being Diabetes reduction app', tag: 'B2C' },
];
const PARTNER_ROLES = ['A GTM expert', 'A team builder', 'A revenue operator'];

export const AboutSameerSection = () => (
  <section className="py-20 lg:py-28 bg-muted px-4 sm:px-6 lg:px-8" data-testid="about-sameer">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-4">Experience-Led Hiring</h2>
      <p className="text-lg text-accent font-semibold text-center mb-12">About Sameer Pant</p>
      <div className="card-depth rounded-2xl p-8 lg:p-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 mb-8">
          <div className="flex-shrink-0 mx-auto lg:mx-0">
            <img
              src="https://customer-assets.emergentagent.com/job_f003bd6d-97d3-4506-aab8-467d347e5410/artifacts/dyg08vn3_Sameer%20Pant2026%20Elevana%20pic.jpeg"
              alt="Sameer Pant" className="w-40 h-40 lg:w-48 lg:h-48 rounded-2xl object-cover object-top shadow-lg" />
          </div>
          <div>
            <p className="text-sm text-accent font-semibold uppercase tracking-wider mb-3">SaaS Sales Leader &middot; GTM Builder &middot; Hiring Expert</p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Sameer has nearly two decades of experience building, leading, and mentoring teams across business and functional roles. He has been an entrepreneur and has worked and built companies and business units at various stage companies like Oracle, Replicon, SignEasy, and Animaker. His contribution as a senior growth and sales leader has been to improve various important processes within SaaS companies, including sales process, transition from B2C to B2B, product roadmap and strategy, inter-team coordination and collaboration, and more. He has hired, grown, and trained thousands of sales professionals. At Elevana Consulting Group, he offers this vast knowledge to upskill sales professionals and startup founders.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Sameer Pant brings first-hand experience of building and scaling SaaS revenue teams — not just recruiting for them. He has led SaaS sales teams himself, hired across the funnel from SDRs to sales leaders, and built teams for outbound sales, enterprise sales, and new market expansion.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {SAMEER_HIGHLIGHTS.map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
              <span className="text-sm text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
        <blockquote className="border-l-4 border-accent pl-6 py-2">
          <p className="text-foreground italic font-medium leading-relaxed">
            "Great SaaS sales hires are not found — they are identified through pattern recognition of real performers."
          </p>
        </blockquote>
      </div>
    </div>
  </section>
);

export const WhyChooseAvantGardeSection = () => (
  <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="why-avant-garde">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-4">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">Why Choose Avant-Garde?</h2>
      </div>
      <p className="text-xl text-accent font-semibold text-center mb-6">Operator. Builder. Advisor.</p>
      <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-6 leading-relaxed">
        Avant-Garde is led by Sameer Pant, a seasoned sales and business leader with 20+ years of experience across Telecom, IT Services, Consulting, SaaS, and Entrepreneurship.
      </p>
      <p className="text-foreground font-semibold text-center max-w-2xl mx-auto mb-16">
        What makes this firm different is simple: <span className="text-accent">It is built by someone who has done the job — not just hired for it.</span>
      </p>

      {/* Leadership & Operating Experience */}
      <div className="card-depth rounded-2xl p-8 lg:p-10 mb-8" data-testid="leadership-experience">
        <div className="w-12 h-1 bg-accent rounded-full mb-6" />
        <h3 className="text-2xl font-bold text-foreground mb-6">Leadership & Operating Experience</h3>
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">20+ years in sales, with 16+ years in mid and senior leadership roles</p>
          <div>
            <p className="text-foreground font-medium mb-2">Built and led teams selling to:</p>
            <div className="flex flex-wrap gap-3 mb-4">
              {SELLING_TARGETS.map((item) => (
                <span key={item} className="px-4 py-1.5 rounded-full bg-primary/5 text-sm font-medium text-primary border border-primary/10">{item}</span>
              ))}
            </div>
          </div>
          <p className="text-foreground font-medium">Created and executed India GTM strategies for multiple businesses</p>
          <div>
            <p className="text-foreground font-medium mb-3">Built high-performing teams across:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {TEAM_AREAS.map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Builder of Teams */}
      <div className="card-depth rounded-2xl p-8 lg:p-10 mb-8" data-testid="builder-teams">
        <div className="w-12 h-1 bg-accent rounded-full mb-6" />
        <h3 className="text-2xl font-bold text-foreground mb-6">Builder of Teams & Business Units</h3>
        <div className="space-y-4">
          {BUILDER_ACHIEVEMENTS.map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">{item}</span>
            </div>
          ))}
          <div className="mt-4">
            <p className="text-foreground font-medium mb-3">Scaled revenue teams across:</p>
            <div className="flex flex-wrap gap-3">
              {SCALING_ENVS.map((item) => (
                <span key={item} className="px-4 py-1.5 rounded-full bg-accent/10 text-sm font-medium text-accent border border-accent/15">{item}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Functional & Growth */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="card-depth rounded-2xl p-8" data-testid="cross-functional">
          <div className="w-12 h-1 bg-accent rounded-full mb-6" />
          <h3 className="text-xl font-bold text-foreground mb-4">Cross-Functional SaaS Expertise</h3>
          <p className="text-muted-foreground mb-4 text-sm leading-relaxed">Sameer has worked across the full SaaS lifecycle, collaborating deeply with:</p>
          <div className="grid grid-cols-2 gap-3">
            {CROSS_FUNCTIONAL.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-5 text-sm leading-relaxed">He has also dotted-line managed entire SaaS organizations, aligning teams across Delivery, Customer Success, Engineering, and Revenue.</p>
          <div className="mt-4 bg-accent/5 rounded-lg p-4 border border-accent/15">
            <p className="text-sm text-foreground font-medium">Result: Strong ability to identify leaders who can operate across functions — not in silos.</p>
          </div>
        </div>
        <div className="card-depth rounded-2xl p-8" data-testid="growth-strategy">
          <div className="w-12 h-1 bg-accent rounded-full mb-6" />
          <h3 className="text-xl font-bold text-foreground mb-4">Growth & Strategy Impact</h3>
          <div className="space-y-4">
            {GROWTH_IMPACT.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Entrepreneur & Advisor */}
      <div className="card-depth rounded-2xl p-8 lg:p-10 mb-8" data-testid="entrepreneur-advisor">
        <div className="w-12 h-1 bg-accent rounded-full mb-6" />
        <h3 className="text-2xl font-bold text-foreground mb-6">Entrepreneur & Advisor</h3>
        <div className="space-y-4 mb-6">
          {ADVISOR_POINTS.map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
        <p className="text-foreground font-medium mb-3">Across industries:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {INDUSTRIES.map((item) => (
            <div key={item.name} className="flex items-center gap-3 bg-muted rounded-lg px-4 py-3">
              <span className="text-sm text-muted-foreground">{item.name}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.tag === 'B2B' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>{item.tag}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What This Means */}
      <div className="rounded-2xl p-8 lg:p-10 hero-gradient text-white" data-testid="what-this-means">
        <h3 className="text-2xl font-bold mb-6 text-center">What This Means for You</h3>
        <p className="text-white/80 text-center mb-6 leading-relaxed">When you work with Avant-Garde, you're not just working with a hiring firm.</p>
        <p className="text-white/90 font-medium text-center mb-6">You're partnering with:</p>
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {PARTNER_ROLES.map((item) => (
            <span key={item} className="px-5 py-2 rounded-full bg-white/15 border border-white/25 text-white font-medium text-sm">{item}</span>
          ))}
        </div>
        <p className="text-white/80 text-center mb-6 leading-relaxed">
          Someone who understands: <span className="text-accent font-semibold">"What it takes to hire people who actually deliver results in SaaS."</span>
        </p>
        <blockquote className="text-center">
          <p className="text-white font-semibold text-lg leading-relaxed">
            At Avant-Garde, we don't just find leaders for where your company is today.<br />
            <span className="text-accent">We find leaders who will define where it goes next.</span>
          </p>
        </blockquote>
      </div>
    </div>
  </section>
);
